"use client";

import { IconAlertWarningOutline18, IconArrowRightOutline18, IconEnvelopeOutline18 } from "nucleo-ui-outline-18";
import { ENTERPRISE_CONTACT_URL } from "@/consts";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getTierDisplayName, type TierInfo } from "@/types/billing";

export type PlanLimitReason = "member_limit" | "workflow_limit" | "database_table_limit";

/**
 * Find the cheapest tier that has a higher limit than the current tier.
 * Sorts by price to avoid depending on API response order.
 */
function findNextTier(
  currentTierName: string,
  tiers: TierInfo[],
  getLimit: (t: TierInfo) => number,
): TierInfo | undefined {
  const current = tiers.find(t => t.name === currentTierName);
  if (!current) return tiers.find(t => t.name === "pro");
  if (getLimit(current) === 0) return undefined; // already unlimited

  const sorted = [...tiers].sort((a, b) => a.priceUSDCents - b.priceUSDCents);
  for (const t of sorted) {
    if (t.priceUSDCents <= current.priceUSDCents) continue;
    const limit = getLimit(t);
    if (limit === 0 || limit > getLimit(current)) return t;
  }
  return undefined;
}

const LIMIT_CONFIG: Record<
  PlanLimitReason,
  {
    title: string;
    unitSingular: string;
    unitPlural: string;
    usageNote?: string;
    getNextTier: (currentTierName: string, tiers: TierInfo[]) => TierInfo | undefined;
    getNextTierValue: (tier: TierInfo) => number;
    enterpriseNoun: string;
  }
> = {
  member_limit: {
    title: "Member limit reached",
    unitSingular: "member",
    unitPlural: "members",
    usageNote: " (including pending invites)",
    getNextTier: (currentTierName, tiers) => findNextTier(currentTierName, tiers, t => t.maxUsers),
    getNextTierValue: tier => tier.maxUsers,
    enterpriseNoun: "members",
  },
  workflow_limit: {
    title: "Workflow limit reached",
    unitSingular: "active workflow",
    unitPlural: "active workflows",
    getNextTier: (currentTierName, tiers) => findNextTier(currentTierName, tiers, t => t.maxActiveWorkflows),
    getNextTierValue: tier => tier.maxActiveWorkflows,
    enterpriseNoun: "active workflows",
  },
  database_table_limit: {
    title: "Database table limit reached",
    unitSingular: "table",
    unitPlural: "tables",
    getNextTier: (currentTierName, tiers) => findNextTier(currentTierName, tiers, t => t.maxDatabaseTables),
    getNextTierValue: tier => tier.maxDatabaseTables,
    enterpriseNoun: "tables",
  },
};

export interface PlanLimitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason: PlanLimitReason;
  currentTier: string;
  currentUsage: number;
  currentLimit: number; // 0 = unlimited
  tiers: TierInfo[];
  onUpgrade: () => void;
}

export function PlanLimitModal({
  open,
  onOpenChange,
  reason,
  currentTier,
  currentUsage,
  currentLimit,
  tiers,
  onUpgrade,
}: PlanLimitModalProps) {
  const config = LIMIT_CONFIG[reason];
  const nextTier = config.getNextTier(currentTier, tiers);
  const isEnterprise = currentTier === "enterprise";
  const currentDisplayName = getTierDisplayName(currentTier);
  const unit = currentLimit === 1 ? config.unitSingular : config.unitPlural;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <IconAlertWarningOutline18 className="h-6 w-6 text-amber-500" />
          </div>
          <DialogTitle className="heading-lg text-lg">{config.title}</DialogTitle>
          <DialogDescription className="body-sm mt-1">
            Your <strong>{currentDisplayName}</strong> plan allows{" "}
            <strong>
              {currentLimit} {unit}
            </strong>
            . You currently have <strong>{currentUsage}</strong>
            {config.usageNote ?? ""}.
          </DialogDescription>
        </DialogHeader>

        {nextTier && !isEnterprise ? (
          <div className="space-y-4">
            <div className="border-ds-border-default dark:bg-ds-neutral-800/50 rounded-xl border bg-zinc-50 p-4">
              <p className="text-ds-text-subtle dark:text-ds-text-disabled text-sm">
                Upgrade to <strong className="text-ds-text-primary">{getTierDisplayName(nextTier.name)}</strong> for{" "}
                <strong className="text-ds-text-primary">
                  {config.getNextTierValue(nextTier) === 0
                    ? `unlimited ${config.unitPlural}`
                    : `up to ${config.getNextTierValue(nextTier)} ${config.unitPlural}`}
                </strong>
                {nextTier.priceUSDCents > 0 && (
                  <span className="text-ds-text-muted"> — ${nextTier.priceUSDCents / 100}/mo</span>
                )}
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="white" onClick={() => onOpenChange(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  onUpgrade();
                }}
                className="flex-1"
              >
                <span className="flex items-center justify-center gap-2">
                  Upgrade Plan
                  <IconArrowRightOutline18 className="h-3.5 w-3.5" />
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border-ds-border-default dark:bg-ds-neutral-800/50 rounded-xl border bg-zinc-50 p-4">
              <p className="text-ds-text-subtle dark:text-ds-text-disabled text-sm">
                Need more {config.enterpriseNoun}? Contact the B3 team to discuss your Enterprise plan options.
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="white" onClick={() => onOpenChange(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={() => window.open(ENTERPRISE_CONTACT_URL, "_blank")} className="flex-1">
                <span className="flex items-center justify-center gap-2">
                  <IconEnvelopeOutline18 className="h-3.5 w-3.5" />
                  Contact B3
                </span>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
